import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IListadoCategoria,
  IFilter,
  IQueryParam,
  IPopulate,
  ITipoListadoCategoria,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { ListadoCategoriasService } from '../../../../auxiliares/servicios/http/listado-categorias.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { CrearEditarListadoCategoriaComponent } from '../crear-editar-listado-categoria/crear-editar-listado-categoria.component';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { formatearCategoria } from '../categoria-display';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { estadoHtml } from '../../../../auxiliares/tabla/celdas';

@Component({
  selector: 'app-listado-listado-categorias',
  templateUrl: './listado-listado-categorias.component.html',
  styleUrl: './listado-listado-categorias.component.scss',
  imports: [AuxiliaresModule],
})
export class ListadoListadoCategoriasComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(ListadoCategoriasService);
  private formularios = inject(FormularioDialogService);

  @Input() categorias?: ITipoListadoCategoria[];
  /** Tooltip personalizado para el botón de crear */
  @Input() tooltipCrear: string | null = null;
  /** Título personalizado para el diálogo de crear/editar */
  @Input() tituloCrear: string | null = null;
  // Tabla
  public name = 'ListadoListadoCategoriasComponent';
  public listadoCategorias?: IListadoCategoria[];
  public totalCount = 0;
  public columnas?: IColumnas<IListadoCategoria>[];
  public listadoCategorias$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso(
      'Administración - Crear listado categorias',
    ),
    tooltip: 'Crear categoría',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: ['nombre'],
  };

  // FILTROS
  public cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public categoriaFiltro: IFiltroTabla = {
    elementos: [],
    filter: { field: 'categoria' },
    label: 'Categoría',
    selectLabel: 'label',
    selectValue: 'value',
    tipo: 'select',
  };
  public global: IFiltroTabla = {
    elementos: [
      { label: 'Global', value: true },
      { label: 'No global', value: false },
    ],
    filter: { field: 'global' },
    selectLabel: 'label',
    selectValue: 'value',
    label: 'Global',
    tipo: 'select',
    rapido: true,
  };
  public filtros: IFiltroTabla[] = [this.categoriaFiltro, this.global];

  // Query
  public filter: IFilter<IListadoCategoria> = {};
  public populate: IPopulate[] = [{ path: 'cliente', select: 'nombre' }];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: false,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    const filter: IFilter<IListadoCategoria> = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    if (!filter.categoria) {
      filter.categoria = { $in: this.categorias } as any;
    }
    this.query.filter = JSON.stringify(filter);
    this.listadoCategorias$?.unsubscribe();
    this.listadoCategorias$ = this.lista2
      .listadoCategorias(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.listadoCategorias = data.datos;
      });
  }

  public crear() {
    const res = this.formularios.abrir(CrearEditarListadoCategoriaComponent, {
      tamano: 'mediano',
      data: { categorias: this.categorias, titulo: this.tituloCrear },
    });
    res.afterClosed().subscribe(async () => {
      await this.listar();
    });
  }

  public async editar(listadoCategoria: IListadoCategoria) {
    const res = this.formularios.abrir(CrearEditarListadoCategoriaComponent, {
      tamano: 'mediano',
      data: {
        edit: listadoCategoria,
        categorias: this.categorias,
        titulo: this.tituloCrear,
      },
    });
    if (res) {
      res.afterClosed().subscribe(async () => {
        await this.listar();
      });
    }
  }

  public async eliminar(listadoCategoria: IListadoCategoria) {
    const res = await this.dialogService.confirm(
      'Eliminar categoría',
      `¿Querés eliminar la categoría ${listadoCategoria.nombre}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(listadoCategoria._id!);
      this.helper.notifSuccess('Categoría eliminada');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  private setColumnas() {
    const columnas: IColumnas<IListadoCategoria>[] = [
      {
        header: { label: 'Nombre', sortable: true, sort: 'nombre' },
        row: {
          html: (dato) => {
            let html = `<div class="tabla-celda-col"><span class="tabla-principal">${escaparHtml(dato.nombre)}</span>`;
            const marcas: string[] = [];
            if (dato.global)
              marcas.push(
                estadoHtml({ texto: 'Global', tono: 'info', icono: 'public' }),
              );
            // Sin ícono en docs/ICONOS.md para «por defecto»: sólo texto
            if (dato.default)
              marcas.push(estadoHtml({ texto: 'Por defecto', tono: 'neutro' }));
            if (marcas.length)
              html += `<span class="tabla-celda-fila">${marcas.join('')}</span>`;
            return html + '</div>';
          },
        },
      },
      {
        header: { label: 'Categoría', sortable: true, sort: 'categoria' },
        row: { parse: (dato) => formatearCategoria(dato.categoria) },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente' },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        acciones: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: (dato) =>
              !this.helper.tienePermiso(
                'Administración - Editar listado categorias',
              ) ||
              (dato.global && !this.helper.esNivel0()),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: (dato) =>
              !this.helper.tienePermiso(
                'Administración - Eliminar listado categorias',
              ) ||
              dato?.default ||
              (dato.global && !this.helper.esNivel0()),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  async ngOnInit() {
    if (this.tooltipCrear) {
      this.botonCrear.tooltip = this.tooltipCrear;
    }
    this.categoriaFiltro.elementos = this.categorias.map((c) => ({
      label: formatearCategoria(c),
      value: c,
    }));
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    this.listar();
  }

  async ngOnDestroy() {
    this.listadoCategorias$?.unsubscribe();
  }
}
